import React, { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { AuthContext } from "../context/AuthContext";
import { FetchContext } from "../context/FetchContext";

const kPendingWorkspaceInvite = "pendingWorkspaceInvite";

const primaryActionButtonStyle = {
    backgroundColor: "var(--app-text-primary)",
    color: "var(--app-bg-surface)",
    textTransform: "none",
    borderRadius: "10px",
    fontFamily: "Inter",
    fontWeight: 500,
    "&:hover": {
        backgroundColor: "var(--app-text-primary)",
        opacity: 0.9,
    },
};

const secondaryActionButtonStyle = {
    color: "var(--app-text-primary)",
    borderColor: "var(--app-border-muted)",
    textTransform: "none",
    borderRadius: "10px",
    fontFamily: "Inter",
    "&:hover": {
        borderColor: "var(--app-text-primary)",
        backgroundColor: "var(--app-bg-subtle)",
    },
};

const getErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 401) return "Please create a Libretto account before accepting this invite.";
    if (status === 403) return "Please verify your email address before accepting this invite.";
    if (status === 404) return "This invite was not found (you may be signed into a different email).";
    if (status === 409) return "This workspace is full, or the invite has already been used.";
    if (status === 410) return "This invite has expired.";
    return "Failed to accept invite. Please try again.";
};

export default function WorkspaceInviteAccept() {
    const authContext = useContext(AuthContext);
    const fetchContext = useContext(FetchContext);
    const navigate = useNavigate();
    const { workspaceId: routeWorkspaceId } = useParams();

    const workspaceId = String(routeWorkspaceId || "").trim();
    const [status, setStatus] = useState("idle"); // idle|accepting|error
    const [message, setMessage] = useState("");
    const isSignedIn = Boolean(authContext?.authState?.userId);

    const clearPendingInvite = () => {
        try {
            window.localStorage.removeItem(kPendingWorkspaceInvite);
        } catch {}
    };

    const continueToAuth = (path) => {
        if (!workspaceId) return;
        try {
            window.localStorage.setItem(kPendingWorkspaceInvite, workspaceId);
        } catch {}
        navigate(path);
    };

    const leaveInvite = () => {
        clearPendingInvite();
        const destination = authContext.isUserAuthenticated?.() ? "/dashboard" : "/";
        navigate(destination, { replace: true });
    };

    const acceptInvite = async () => {
        if (!workspaceId) {
            setStatus("error");
            setMessage("This invite link is missing its workspace.");
            return;
        }

        setStatus("accepting");
        setMessage("");

        const token = (await authContext.refreshToken?.()) || (await authContext.getFreshToken?.());
        if (!token) {
            setStatus("error");
            setMessage("Please sign in before accepting this invite.");
            return;
        }

        try {
            const { data } = await fetchContext.authAxios.post(
                `/workspaces/${encodeURIComponent(workspaceId)}/invites/accept`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const acceptedWorkspaceId = String(data?.workspaceId || workspaceId).trim();
            authContext.upsertWorkspaceInList?.({
                workspaceId: acceptedWorkspaceId,
                name: String(data?.workspaceName || "").trim(),
                role: String(data?.role || "").trim(),
            });
            authContext.setCurrentWorkspaceId?.(acceptedWorkspaceId);
            clearPendingInvite();
            navigate("/dashboard", { replace: true });
        } catch (error) {
            setStatus("error");
            setMessage(getErrorMessage(error));
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                padding: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "#F7F8FC",
            }}
        >
            <Box
                sx={{
                    maxWidth: "520px",
                    width: "100%",
                    backgroundColor: "#FFFFFF",
                    borderRadius: "12px",
                    padding: "24px",
                    border: "1px solid #E5E7EB",
                    boxShadow: "0px 20px 60px rgba(15, 23, 42, 0.08)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: "Inter",
                        fontSize: 26,
                        fontWeight: 600,
                        lineHeight: "32px",
                        letterSpacing: "-0.2px",
                        color: "#111111",
                    }}
                >
                    Workspace invite
                </Typography>

                {!workspaceId ? (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#C63636" }}>
                            This invite link is missing its workspace.
                        </Typography>
                        <Button
                            variant="outlined"
                            sx={{ ...secondaryActionButtonStyle, marginTop: 2 }}
                            onClick={leaveInvite}
                        >
                            Return to Libretto
                        </Button>
                    </Box>
                ) : !authContext?.authReady ? (
                    <Box sx={{ marginTop: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <CircularProgress size={18} />
                        <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#6B7280" }}>
                            Checking your account…
                        </Typography>
                    </Box>
                ) : !isSignedIn ? (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#6B7280" }}>
                            Sign up or sign in with the email address that received this invite.
                        </Typography>
                        <Box sx={{ marginTop: 2, display: "flex", gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={() => continueToAuth("/signup")}
                                sx={primaryActionButtonStyle}
                            >
                                Sign up
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => continueToAuth("/login")}
                                sx={secondaryActionButtonStyle}
                            >
                                Sign in
                            </Button>
                        </Box>
                    </Box>
                ) : status === "error" ? (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#C63636" }}>
                            {message}
                        </Typography>
                        <Box sx={{ marginTop: 2, display: "flex", gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={acceptInvite}
                                sx={primaryActionButtonStyle}
                            >
                                Try again
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={leaveInvite}
                                sx={secondaryActionButtonStyle}
                            >
                                Return to Libretto
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ marginTop: 2 }}>
                        <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#6B7280" }}>
                            Accept this invite to add the workspace to your Libretto account.
                        </Typography>
                        {authContext.authState?.email ? (
                            <Typography sx={{ marginTop: 1, fontFamily: "Inter", fontSize: 13, color: "#6B7280" }}>
                                Signed in as {authContext.authState.email}
                            </Typography>
                        ) : null}
                        <Box sx={{ marginTop: 2, display: "flex", gap: 1 }}>
                            <Button
                                variant="contained"
                                disabled={status === "accepting"}
                                onClick={acceptInvite}
                                sx={primaryActionButtonStyle}
                            >
                                {status === "accepting" ? "Accepting…" : "Accept invite"}
                            </Button>
                            <Button
                                variant="text"
                                disabled={status === "accepting"}
                                onClick={leaveInvite}
                                sx={secondaryActionButtonStyle}
                            >
                                Not now
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
